import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import type { ApolloError } from '@apollo/client/errors';

import {
  metricFilterOperators,
  defaultOperators,
} from '../components/DashboardTable/constants';
import {
  FilterWithValue,
  FrontendFilterType,
  MetricFilterOperatorKey,
  MetricFilterOperatorLabel,
} from '../components/DashboardTable/types';
import { getTitle } from '../components/DashboardTable/utils';
import { useSnackbar } from '../providers/snackbar/hook/useSnackbar';
import {
  GET_AVAILABLE_FILTERS_FOR_METRIC,
  GET_FILTERS,
  GET_SHOPIFY_FILTERS,
  GET_MAGENTO_FILTERS,
} from '../utils/gql/queries';
import {
  ConnectorType,
  Filter,
  FilterValueType,
  Metric,
  Query,
  QueryGetAvailableFiltersArgs,
  QueryGetAvailableFiltersForMetricArgs,
} from '../utils/gql/types';
import { useConnectors } from '../providers/connectors/hook/useConnectors';
import { ensureFidodbGeoSplits } from '../components/DatabaseConnector/ensureFidodbGeoSplits';

interface UseFiltersDataProps {
  skipGoogleAdsFilters?: boolean;
  skipGoogleAnalyticsFilters?: boolean;
  skipFacebookFilters?: boolean;
  skipShopifyFilters?: boolean;
  skipMagentoFilters?: boolean;
  skipGoogleSearchConsoleFilters?: boolean;
  googleAdsMetricId?: string;
  shopifyFilterValues?: { key: string; values: string[] }[];
  magentoFilterValues?: { key: string; values: string[] }[];
  searchValue?: string;
  selectedMetrics?: Metric[];
}

const useFiltersData = ({
  skipFacebookFilters,
  skipShopifyFilters,
  skipMagentoFilters,
  skipGoogleAdsFilters,
  skipGoogleAnalyticsFilters,
  skipGoogleSearchConsoleFilters,
  googleAdsMetricId,
  shopifyFilterValues,
  magentoFilterValues,
  searchValue,
  selectedMetrics,
}: UseFiltersDataProps): { filters: FilterWithValue[]; loading: boolean } => {
  const snackbar = useSnackbar();
  const { dbSplits = [] } = useConnectors();

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const { data: googleAdsFiltersData, loading: googleAdsFiltersLoading } =
    useQuery<
      Pick<Query, 'getAvailableFiltersForMetric'>,
      QueryGetAvailableFiltersForMetricArgs
    >(GET_AVAILABLE_FILTERS_FOR_METRIC, {
      variables: { metricId: googleAdsMetricId ?? '' },
      skip: !googleAdsMetricId || skipGoogleAdsFilters,
      onError,
      // Rimuovo fetchPolicy: 'network-only' per usare la cache di Apollo
    });

  const { data: facebookFiltersData, loading: facebookFiltersLoading } =
    useQuery<Pick<Query, 'getFilters'>, QueryGetAvailableFiltersArgs>(
      GET_FILTERS,
      {
        variables: { connectorType: ConnectorType.FACEBOOK },
        skip: skipFacebookFilters,
        onError,
      },
    );

  const {
    data: googleAnalyticsFiltersData,
    loading: googleAnalyticsFiltersLoading,
  } = useQuery<Pick<Query, 'getFilters'>, QueryGetAvailableFiltersArgs>(
    GET_FILTERS,
    {
      variables: { connectorType: ConnectorType.GOOGlE_ANALYTICS4 },
      skip: skipGoogleAnalyticsFilters,
      onError,
    },
  );

  const {
    data: googleSearchConsoleFiltersData,
    loading: googleSearchConsoleFiltersLoading,
  } = useQuery<Pick<Query, 'getFilters'>, QueryGetAvailableFiltersArgs>(
    GET_FILTERS,
    {
      variables: { connectorType: ConnectorType.GOOGLE_SEARCH_CONSOLE },
      skip: skipGoogleSearchConsoleFilters,
      onError,
    },
  );

  const { data: shopifyFiltersData, loading: shopifyFiltersLoading } = useQuery<
    Pick<Query, 'getShopifyFilters'>
  >(GET_SHOPIFY_FILTERS, {
    onError,
    skip: skipShopifyFilters,
  });

  const { data: magentoFiltersData, loading: magentoFiltersLoading } = useQuery<
    Pick<Query, 'getMagentoFilters'>
  >(GET_MAGENTO_FILTERS, {
    onError,
    skip: skipMagentoFilters,
  });

  const backendFilters = useMemo(() => {
    let filters: Filter[] = [];

    const shopifyFilters = shopifyFiltersData?.getShopifyFilters.map(
      (filter) => {
        const valuesFromMetrics =
          (shopifyFilterValues ?? []).find(({ key }) => key === filter.key)
            ?.values ?? [];

        const availableValues = valuesFromMetrics.map((val) => ({
          label: getTitle(val),
          key: val,
        }));

        return {
          type: ConnectorType.SHOPIFY,
          id: filter.key,
          key: filter.key,
          label: getTitle(filter.key),
          operators: filter.availableOperators,
          expectedReturnValueType: filter.expectedReturnValueType,
          availableValues,
          // Fallback: se non abbiamo valori pre-caricati da filtersOptions,
          // abilita la richiesta on-demand (GET_FILTER_AVAILABLE_VALUES)
          shouldAvailableValuesBeRequested: availableValues.length === 0,
        } as Filter;
      },
    );

    const magentoFilters = magentoFiltersData?.getMagentoFilters.map(
      (filter) => {
        const valuesFromMetrics =
          (magentoFilterValues ?? []).find(({ key }) => key === filter.key)
            ?.values ?? [];

        const availableValues = valuesFromMetrics.map((val) => ({
          label: getTitle(val),
          key: val,
        }));

        return {
          type: ConnectorType.MAGENTO,
          id: filter.key,
          key: filter.key,
          label: getTitle(filter.key),
          operators: filter.availableOperators,
          expectedReturnValueType: filter.expectedReturnValueType,
          availableValues,
          shouldAvailableValuesBeRequested: availableValues.length === 0,
        } as Filter;
      },
    );

    // Aggiungi gli splits del Database come dimensioni filtrabili
    const dbFilters: Filter[] = ensureFidodbGeoSplits(dbSplits || []).map((s) => {
      const isGeoFilter =
        s.key === 'fidodb.provincia_cliente' ||
        s.key === 'fidodb.regione_cliente' ||
        s.key === 'fidodb.citta_cliente';
      return {
        type: ConnectorType.DATABASE,
        id: s.key,
        key: s.key,
        label: s.label || getTitle(s.key),
        operators: defaultOperators,
        expectedReturnValueType: FilterValueType.ArrayStr,
        // Provincia/regione/città: valori DISTINCT (o regioni IT statiche) dal backend.
        shouldAvailableValuesBeRequested: isGeoFilter,
      };
    });

    // Aggiungi filtri specifici per i search terms unificati (PMax + Search/Shopping)
    const searchTermsFilters: Filter[] = [
      {
        type: ConnectorType.GOOGLE_ADS,
        id: 'search_term_hardcoded',
        key: 'search_term',
        label: 'Search Term',
        operators: [
          { key: 'IN', label: 'Is' },
          { key: 'NOT_IN', label: 'Is Not' },
          { key: 'CONTAIN', label: 'Contains' },
          { key: 'NOT_CONTAIN', label: 'Does Not Contain' },
        ],
        expectedReturnValueType: FilterValueType.ArrayStr,
        shouldAvailableValuesBeRequested: false,
      },
      {
        type: ConnectorType.GOOGLE_ADS,
        id: 'campaignType',
        key: 'campaignType',
        label: 'Campaign Type',
        operators: defaultOperators,
        expectedReturnValueType: FilterValueType.ArrayStr,
        shouldAvailableValuesBeRequested: false,
        availableValues: [
          { key: 'SEARCH', label: 'Search' },
          { key: 'SHOPPING', label: 'Shopping' },
          { key: 'PERFORMANCE_MAX', label: 'Performance Max' },
          { key: 'DISPLAY', label: 'Display' },
          { key: 'VIDEO', label: 'Video' },
        ],
      },
      {
        type: ConnectorType.GOOGLE_ADS,
        id: 'sourceView',
        key: 'sourceView',
        label: 'Source View',
        operators: defaultOperators,
        expectedReturnValueType: FilterValueType.ArrayStr,
        shouldAvailableValuesBeRequested: false,
        availableValues: [
          { key: 'search_term_view', label: 'Search/Shopping Terms' },
          { key: 'campaign_search_term_view', label: 'Performance Max Terms' },
        ],
      },
    ];

    if (googleAdsFiltersData?.getAvailableFiltersForMetric) {
      filters = [
        ...filters,
        ...googleAdsFiltersData?.getAvailableFiltersForMetric
          .filter((filter) => filter.key !== 'search_term') // Escludi search_term dal database per evitare duplicati
          .slice()
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }

    if (shopifyFilters) {
      filters = [
        ...filters,
        ...shopifyFilters
          .slice()
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }

    if (magentoFilters) {
      filters = [
        ...filters,
        ...magentoFilters
          .slice()
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }

    if (dbFilters?.length) {
      filters = [...filters, ...dbFilters];
    }

    if (searchTermsFilters?.length) {
      filters = [...filters, ...searchTermsFilters];
    }

    if (facebookFiltersData?.getFilters) {
      filters = [
        ...filters,
        ...facebookFiltersData?.getFilters
          .slice()
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }

    if (googleAnalyticsFiltersData?.getFilters) {
      filters = [
        ...filters,
        ...googleAnalyticsFiltersData?.getFilters
          .slice()
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }

    if (googleSearchConsoleFiltersData?.getFilters) {
      filters = [
        ...filters,
        ...googleSearchConsoleFiltersData.getFilters
          .slice()
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }

    return filters
      .map((filter) => {
        return {
          ...filter,
          values: [],
          type: FrontendFilterType.SimpleFilter,
          connectorType: filter.type,
        };
      })
      .filter(
        ({ key }) => key !== 'segments.date' && key !== 'order_creation_date',
      );
  }, [
    googleAdsFiltersData?.getAvailableFiltersForMetric,
    googleAnalyticsFiltersData?.getFilters,
    googleSearchConsoleFiltersData?.getFilters,
    facebookFiltersData?.getFilters,
    shopifyFilterValues,
    shopifyFiltersData?.getShopifyFilters,
    magentoFilterValues,
    magentoFiltersData?.getMagentoFilters,
    dbSplits,
  ]);

  const metricFilters: FilterWithValue[] = useMemo(
    () =>
      selectedMetrics
        ?.filter((metric) => metric.key !== 'eventName') // Rimuovi eventName dalle metriche
        ?.map(({ id, label, type, key }): FilterWithValue => {
          // eventName è una dimensione (split), non una metrica numerica
          const isEventNameDimension = key === 'eventName';

          // Per eventName usa solo operatori CONTAIN, per gli altri usa defaultOperators
          const eventNameOperators = [
            {
              key: MetricFilterOperatorKey.Contains,
              label: MetricFilterOperatorLabel.Contains,
            },
            {
              key: MetricFilterOperatorKey.NotContains,
              label: MetricFilterOperatorLabel.NotContains,
            },
          ];

          return {
            id,
            key,
            type: FrontendFilterType.MetricFilter,
            label,
            connectorType: type,
            operators: isEventNameDimension
              ? eventNameOperators
              : defaultOperators,
            values: [],
            expectedReturnValueType: isEventNameDimension
              ? FilterValueType.ArrayStr
              : FilterValueType.ArrayInt,
            shouldAvailableValuesBeRequested: false,
          };
        }) ?? [],
    [selectedMetrics],
  );

  const filteredFilters = useMemo(() => {
    const allFilters: FilterWithValue[] = [...metricFilters, ...backendFilters];

    return searchValue
      ? allFilters.filter(({ label }) =>
          label.toLowerCase().includes(searchValue.toLowerCase()),
        )
      : allFilters;
  }, [backendFilters, metricFilters, searchValue]);

  const isLoader =
    googleAdsFiltersLoading ||
    shopifyFiltersLoading ||
    magentoFiltersLoading ||
    facebookFiltersLoading ||
    googleAnalyticsFiltersLoading ||
    googleSearchConsoleFiltersLoading;

  return {
    filters: filteredFilters,
    loading: isLoader,
  };
};

export default useFiltersData;
